/**
 * PromptDragHandler
 *
 * Handles prompt sphere dragging, neighbor recentering after drag,
 * edge cleanup (removing short edges), and merging collinear prompts.
 */

import * as THREE from 'three';
import { LinePrompt, PromptConnection, DetectedLine } from './types';
import { CoordinateTransformer } from '../utils/CoordinateTransformer';
import { DensitySnapper } from './DensitySnapper';
import { PromptAligner } from './PromptAligner';

/** Threshold in pixels for snapping to a detected line */
const SNAP_THRESHOLD_PIXELS = 30;

/**
 * Result of finding the nearest detected line to snap to
 */
export interface LineSnapResult {
  /** The detected line to snap to */
  line: DetectedLine;
  /** The midpoint of the line (snap target position) */
  snapPoint: THREE.Vector3;
  /** Distance in pixels from the cursor to the line midpoint */
  distancePixels: number;
}

/**
 * State captured when a prompt sphere drag begins.
 */
export interface PromptSphereDragState {
  prompt: LinePrompt;
  originalWorldPoint: THREE.Vector3;
  originalDirection: THREE.Vector3;
  originalPosLength: number | undefined;
  originalNegLength: number | undefined;
  lastUpdateTime: number;
  /** Direction from the previous drag frame (for rotation smoothing) */
  lastFittedDirection: THREE.Vector3 | null;
  /** Saved neighbor positions so they can be restored on cancel */
  neighborSnapshots: Array<{
    prompt: LinePrompt;
    worldPoint: THREE.Vector3;
    screenPosition: THREE.Vector2;
    /** The prompt on the neighbor's far side (away from the dragged prompt) */
    farPrompt: LinePrompt | null;
    /** Which side of the dragged prompt this neighbor is on ('pos' or 'neg') */
    side?: 'pos' | 'neg';
  }>;
  /** Cached far vertices for temporary neighbors, keyed by prompt id.
   *  Populated on first successful computation to prevent jumps on reconnect. */
  tempNeighborFarVertices?: Map<string, THREE.Vector3>;
}

export class PromptDragHandler {
  private promptAligner: PromptAligner;

  constructor(
    private coordinateTransformer: CoordinateTransformer,
    private densitySnapper: DensitySnapper,
  ) {
    this.promptAligner = new PromptAligner(coordinateTransformer);
  }

  /**
   * Pin a world-space point onto the active base plane. With a frame
   * installed, round-trip through local (z = 0) to drop off-plane drift.
   * Without a frame, fall back to the legacy `wp.z = floorHeight` flatten
   * (preserves byte-identical behavior for non-LineTool callers).
   */
  private snapToBasePlane(p: THREE.Vector3, floorHeight: number | null): void {
    const frame = this.coordinateTransformer.getBasePlaneFrame();
    if (frame) {
      const local = this.coordinateTransformer.toBasePlaneLocal(p);
      local.z = 0;
      p.copy(this.coordinateTransformer.fromBasePlaneLocal(local));
    } else if (floorHeight !== null) {
      p.z = floorHeight;
    }
  }

  /**
   * Move a dragged prompt to the mouse position with pixel alignment.
   * Keeps the prompt's original direction and scans perpendicular in the
   * density mask to snap the midpoint onto a real line feature.
   * If detectedLines are provided and the cursor is near a line, snaps to that line instead.
   * Returns the nearest snap line result if snapping is active.
   */
  movePromptToMouse(
    mouse: THREE.Vector2,
    dragState: PromptSphereDragState,
    prompts: LinePrompt[],
    floorHeight: number | null,
    densityMap: ImageData | null,
    positionMap: ImageData | null,
    shiftDown: boolean,
    onShapeUpdate: () => void,
    detectedLines?: DetectedLine[],
    viewportWidth?: number,
    viewportHeight?: number,
    camera?: THREE.Camera,
    ctrlDown?: boolean,
    opts?: {
      /**
       * Rectangle-preserving drag (kind adapters with `rectangleOnly`): the
       * prompt slides ONLY along the perpendicular of its own edge and its
       * direction NEVER changes. Snapping stays available — detected-line
       * snap is restricted to lines parallel to the edge (taking just their
       * perpendicular position), and the density snap runs position-only
       * (no rotation fit). Ctrl disables snapping as usual. The neighbor
       * recentering still runs, keeping the handles at the rectangle's edge
       * midpoints.
       */
      perpendicularOnly?: boolean;
    },
  ): LineSnapResult | null {
    const prompt = dragState.prompt;

    if (opts?.perpendicularOnly) {
      const worldPoint = this.coordinateTransformer.screenToWorld(mouse);
      if (!worldPoint) return null;
      this.snapToBasePlane(worldPoint, floorHeight);

      const noAlignmentSnapping = ctrlDown === true;
      let snapResult: LineSnapResult | null = null;

      // Detected-line snap, rotation-locked: only lines PARALLEL to the
      // prompt's edge (within ~10°) may attract it, and only their
      // perpendicular position component is taken — the side slides onto
      // the feature line without rotating onto it.
      if (!noAlignmentSnapping && detectedLines && viewportWidth && viewportHeight && camera) {
        const nearest = this.findNearestSnapLine(mouse, detectedLines, viewportWidth, viewportHeight, camera);
        if (nearest) {
          const dirDot = Math.abs(nearest.line.direction.clone().normalize().dot(dragState.originalDirection.clone().normalize()));
          if (dirDot > 0.985) {
            worldPoint.copy(nearest.snapPoint);
            this.snapToBasePlane(worldPoint, floorHeight);
            snapResult = nearest;
          }
        }
      }

      prompt.worldPoint.copy(this.projectOntoPerpendicularAxis(worldPoint, dragState));
      prompt.direction.copy(dragState.originalDirection);

      // Density position snap (direction preserved — see alignPromptToPosition):
      // the perpendicular probe scan pulls the side onto the nearest density
      // feature, which keeps the point on the constraint axis. Skipped when a
      // detected line already claimed the position.
      if (!noAlignmentSnapping && snapResult === null) {
        this.densitySnapper.alignPromptToPosition(prompt, densityMap, positionMap, floorHeight);
        prompt.worldPoint.copy(this.projectOntoPerpendicularAxis(prompt.worldPoint, dragState));
        prompt.direction.copy(dragState.originalDirection);
      }

      prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);
      onShapeUpdate();
      if (dragState.neighborSnapshots.length > 0) {
        this.recenterNeighborPrompts(prompt, dragState, prompts, floorHeight, onShapeUpdate);
      } else if (prompt.isTemporary) {
        this.recenterTemporaryNeighbors(prompt, dragState, prompts, floorHeight, onShapeUpdate);
      }
      return snapResult;
    }

    // Ctrl held: disable alignment snapping (but 90° rotation with shift still works)
    const noAlignmentSnapping = ctrlDown === true;

    // Check for snap to detected line if all required params are provided (skip if ctrl held)
    if (!noAlignmentSnapping && detectedLines && viewportWidth && viewportHeight && camera) {
      const snapResult = this.findNearestSnapLine(mouse, detectedLines, viewportWidth, viewportHeight, camera);
      if (snapResult) {
        // Snap to the detected line
        this.snapPromptToLine(prompt, snapResult.line, floorHeight);

        // Live shape update — rebuilds connections
        onShapeUpdate();

        // Recenter adjacent prompts on their edges
        if (dragState.neighborSnapshots.length > 0) {
          this.recenterNeighborPrompts(prompt, dragState, prompts, floorHeight, onShapeUpdate);
        } else if (prompt.isTemporary) {
          this.recenterTemporaryNeighbors(prompt, dragState, prompts, floorHeight, onShapeUpdate);
        }

        return snapResult;
      }
    }

    // No snap target found - use normal density-based alignment

    // Convert mouse to world
    const worldPoint = this.coordinateTransformer.screenToWorld(mouse);
    if (!worldPoint) return null;

    // Pin to the active base plane.
    this.snapToBasePlane(worldPoint, floorHeight);

    // Move prompt to cursor position, reset to original rotation for probing
    prompt.worldPoint.copy(worldPoint);
    prompt.direction.copy(dragState.originalDirection);

    // Snap perpendicular to the original direction + fit rotation from peaks (skip if ctrl held)
    if (!noAlignmentSnapping) {
      this.densitySnapper.alignPromptToPositionAndRotation(prompt, densityMap, positionMap, floorHeight);

      // Smooth rotation between drag frames: blend new direction with
      // previous frame in (u, v) of the active base plane. On the floor
      // plane this is identity in XY; on a wall plane the angles live
      // in the wall's tangent frame so the blend stays in-plane.
      const directionChanged = !prompt.direction.equals(dragState.originalDirection);
      if (directionChanged && dragState.lastFittedDirection) {
        const tf = this.coordinateTransformer;
        const frame = tf.getBasePlaneFrame();
        const prevLocal = frame ? tf.toBasePlaneLocalDir(dragState.lastFittedDirection) : dragState.lastFittedDirection;
        const newLocal = frame ? tf.toBasePlaneLocalDir(prompt.direction) : prompt.direction;
        const prevAngle = Math.atan2(prevLocal.y, prevLocal.x);
        const newAngle = Math.atan2(newLocal.y, newLocal.x);
        let angleDiff = newAngle - prevAngle;
        while (angleDiff > Math.PI / 2) angleDiff -= Math.PI;
        while (angleDiff < -Math.PI / 2) angleDiff += Math.PI;
        const blended = prevAngle + angleDiff * 0.5;
        const blendedLocal = new THREE.Vector3(Math.cos(blended), Math.sin(blended), 0);
        const blendedWorld = frame ? tf.fromBasePlaneLocalDir(blendedLocal) : blendedLocal;
        prompt.direction.copy(blendedWorld.normalize());
      }
      if (directionChanged) {
        dragState.lastFittedDirection = prompt.direction.clone();
      }
    }

    // Shift held: snap to 90-degree angles relative to the neighbor on the side we moved to
    // This works even with ctrl held (ctrl only disables alignment, not rotation)
    if (shiftDown) {
      // Determine which side of the original edge the prompt moved to
      const displacement = new THREE.Vector3().subVectors(prompt.worldPoint, dragState.originalWorldPoint);
      const alongOriginalDir = displacement.dot(dragState.originalDirection);
      const targetSide: 'pos' | 'neg' = alongOriginalDir >= 0 ? 'pos' : 'neg';

      // Find the neighbor on that side
      let neighborDirection: THREE.Vector3 | null = null;
      for (const snap of dragState.neighborSnapshots) {
        if (snap.side === targetSide && snap.prompt && snap.prompt.direction) {
          neighborDirection = snap.prompt.direction.clone();
          break;
        }
      }

      // If we found a neighbor on that side, snap to 90° of its direction
      // Pass null for densityMap when ctrl is held to skip alignment snapping
      if (neighborDirection) {
        this.densitySnapper.alignPromptToAngle(prompt, noAlignmentSnapping ? null : densityMap, noAlignmentSnapping ? null : positionMap, floorHeight, neighborDirection);
      }
    }

    // Update screen position
    prompt.screenPosition = this.coordinateTransformer.worldToScreen(prompt.worldPoint);

    // Live shape update — rebuilds connections
    onShapeUpdate();

    // Recenter adjacent prompts on their edges
    if (dragState.neighborSnapshots.length > 0) {
      this.recenterNeighborPrompts(prompt, dragState, prompts, floorHeight, onShapeUpdate);
    } else if (prompt.isTemporary) {
      // For temporary prompts without pre-captured neighbors, use fresh connections
      this.recenterTemporaryNeighbors(prompt, dragState, prompts, floorHeight, onShapeUpdate);
    }

    return null;
  }

  /**
   * Project a drag candidate onto the axis through the drag origin
   * PERPENDICULAR to the prompt's original direction, in the active base
   * plane — the rectangle-preserving movement: the prompt's edge translates
   * in/out, never along itself and never rotating.
   */
  private projectOntoPerpendicularAxis(candidate: THREE.Vector3, dragState: PromptSphereDragState): THREE.Vector3 {
    const tf = this.coordinateTransformer;
    const frame = tf.getBasePlaneFrame();
    const origin = frame ? tf.toBasePlaneLocal(dragState.originalWorldPoint) : dragState.originalWorldPoint.clone();
    const cand = frame ? tf.toBasePlaneLocal(candidate) : candidate.clone();
    const dir = frame ? tf.toBasePlaneLocalDir(dragState.originalDirection) : dragState.originalDirection.clone();
    const dirLen = Math.hypot(dir.x, dir.y);
    if (dirLen < 1e-9) return candidate;
    // In-plane perpendicular of the edge direction.
    const nx = -dir.y / dirLen;
    const ny = dir.x / dirLen;
    const d = (cand.x - origin.x) * nx + (cand.y - origin.y) * ny;
    const local = new THREE.Vector3(origin.x + nx * d, origin.y + ny * d, 0);
    return frame ? tf.fromBasePlaneLocal(local) : new THREE.Vector3(local.x, local.y, candidate.z);
  }

  /**
   * After moving a prompt, recenter its neighboring prompts so each sits at
   * the midpoint of its wall edge.
   */
  private recenterNeighborPrompts(draggedPrompt: LinePrompt, dragState: PromptSphereDragState, prompts: LinePrompt[], floorHeight: number | null, onShapeUpdate: () => void): void {
    const snapshots = dragState.neighborSnapshots;
    if (snapshots.length === 0) return;

    for (const snap of snapshots) {
      const neighbor = snap.prompt;
      if (neighbor === draggedPrompt) continue;

      // Near vertex: intersection of dragged prompt's line with neighbor's line
      const nearVertex = PromptAligner.intersectInfiniteLines(draggedPrompt.worldPoint, draggedPrompt.direction, neighbor.worldPoint, neighbor.direction, this.coordinateTransformer);

      // Far vertex: intersection of neighbor's line with its far-side prompt's line
      let farVertex: THREE.Vector3 | null = null;
      if (snap.farPrompt) {
        farVertex = PromptAligner.intersectInfiniteLines(neighbor.worldPoint, neighbor.direction, snap.farPrompt.worldPoint, snap.farPrompt.direction, this.coordinateTransformer);
      }

      // If no far prompt (e.g. external polygon connection), use the connection point directly
      if (!farVertex) {
        const conns = neighbor.connections;
        if (conns) {
          for (const side of ['positive', 'negative'] as const) {
            const c: PromptConnection | undefined = conns[side];
            if (!c || !c.point) continue;
            const targetIdx = c.targetPromptIndex;
            // Use the connection point if it points away from the dragged prompt
            // (either external connection with index -1, or a different prompt)
            const targetPrompt = targetIdx >= 0 && targetIdx < prompts.length ? prompts[targetIdx] : null;
            if (targetPrompt !== draggedPrompt) {
              farVertex = c.point;
              break;
            }
          }
        }
      }

      if (nearVertex && farVertex) {
        const midpoint = nearVertex.clone().add(farVertex).multiplyScalar(0.5);
        this.snapToBasePlane(midpoint, floorHeight);
        neighbor.worldPoint.copy(midpoint);
        neighbor.screenPosition = this.coordinateTransformer.worldToScreen(midpoint);
      } else if (nearVertex) {
        // Only near vertex available — just project neighbor onto nearest point
        // on its own line closest to the dragged prompt's new intersection
        neighbor.worldPoint.copy(nearVertex);
        this.snapToBasePlane(neighbor.worldPoint, floorHeight);
        neighbor.screenPosition = this.coordinateTransformer.worldToScreen(neighbor.worldPoint);
      }
    }

    // Rebuild shape with updated neighbor positions
    onShapeUpdate();
  }

  /**
   * Recenter temporary neighbors of a dragged temporary prompt using fresh connections.
   * Unlike regular prompts whose neighbors are captured at drag start, temporary prompts
   * may not have connections at mousedown time, so we read them live after onShapeUpdate.
   */
  private recenterTemporaryNeighbors(draggedPrompt: LinePrompt, dragState: PromptSphereDragState, prompts: LinePrompt[], floorHeight: number | null, onShapeUpdate: () => void): void {
    const conns = draggedPrompt.connections;
    if (!conns) return;

    // Lazily initialize the far vertex cache
    if (!dragState.tempNeighborFarVertices) {
      dragState.tempNeighborFarVertices = new Map();
    }
    const farVertexCache = dragState.tempNeighborFarVertices;

    let moved = false;
    for (const side of ['positive', 'negative'] as const) {
      const conn: PromptConnection | undefined = conns[side];
      if (!conn) continue;
      const neighborIdx = conn.targetPromptIndex;
      if (neighborIdx < 0 || neighborIdx >= prompts.length) continue;
      const neighbor = prompts[neighborIdx];
      if (neighbor === draggedPrompt || !neighbor.isTemporary) continue;

      // Near vertex: intersection of dragged prompt's line with neighbor's line
      const nearVertex = PromptAligner.intersectInfiniteLines(draggedPrompt.worldPoint, draggedPrompt.direction, neighbor.worldPoint, neighbor.direction, this.coordinateTransformer);
      if (!nearVertex) continue;

      // Far vertex: use cached value if available, otherwise compute and cache
      let farVertex: THREE.Vector3 | null = farVertexCache.get(neighbor.id) ?? null;

      if (!farVertex) {
        const nConns = neighbor.connections;
        if (nConns) {
          for (const nSide of ['positive', 'negative'] as const) {
            const nConn: PromptConnection | undefined = nConns[nSide];
            if (!nConn) continue;
            const farIdx = nConn.targetPromptIndex;
            if (farIdx >= 0 && farIdx < prompts.length) {
              const farPrompt = prompts[farIdx];
              if (farPrompt === draggedPrompt || farPrompt === neighbor) continue;
              farVertex = PromptAligner.intersectInfiniteLines(neighbor.worldPoint, neighbor.direction, farPrompt.worldPoint, farPrompt.direction, this.coordinateTransformer);
            } else if (nConn.point) {
              // External connection (e.g. polygon edge) — use the connection point directly
              farVertex = nConn.point;
            }
            if (farVertex) break;
          }
        }

        // Cache the far vertex so it survives disconnect/reconnect cycles
        if (farVertex) {
          farVertexCache.set(neighbor.id, farVertex.clone());
        }
      }

      if (nearVertex && farVertex) {
        const midpoint = nearVertex.clone().add(farVertex).multiplyScalar(0.5);
        this.snapToBasePlane(midpoint, floorHeight);
        neighbor.worldPoint.copy(midpoint);
        neighbor.screenPosition = this.coordinateTransformer.worldToScreen(midpoint);
        moved = true;
      } else if (nearVertex) {
        // Only near vertex — snap neighbor to the intersection point
        neighbor.worldPoint.copy(nearVertex);
        this.snapToBasePlane(neighbor.worldPoint, floorHeight);
        neighbor.screenPosition = this.coordinateTransformer.worldToScreen(neighbor.worldPoint);
        moved = true;
      }
    }

    if (moved) {
      onShapeUpdate();
    }
  }

  /**
   * Clean up edges after a prompt has been dragged.
   * 1. Rebuild connections (via onShapeUpdate)
   * 2. Remove short edges (<5cm) only when their neighbors are collinear (will merge)
   * 3. Merge collinear edges
   *
   * Returns the updated prompts array (may have removed elements).
   */
  cleanupEdgesAfterDrag(movedPrompt: LinePrompt, prompts: LinePrompt[], floorHeight: number | null, onShapeUpdate: (prompts: LinePrompt[]) => void): LinePrompt[] {
    return this.promptAligner.cleanupEdgesAroundPrompt(movedPrompt, prompts, floorHeight, onShapeUpdate);
  }

  /**
   * Find the nearest detected line to snap to based on cursor position.
   * Returns null if no line is within the snap threshold.
   */
  findNearestSnapLine(mouse: THREE.Vector2, detectedLines: DetectedLine[], viewportWidth: number, viewportHeight: number, camera: THREE.Camera): LineSnapResult | null {
    if (detectedLines.length === 0) return null;

    let nearestResult: LineSnapResult | null = null;
    let nearestDistance = SNAP_THRESHOLD_PIXELS;

    for (const line of detectedLines) {
      // Skip hidden lines
      if (line.isHidden) continue;

      // Calculate midpoint of the line
      const midpoint = line.start.clone().add(line.end).multiplyScalar(0.5);

      // Project midpoint to screen space
      const projected = midpoint.clone().project(camera);
      const screenX = ((projected.x + 1) * viewportWidth) / 2;
      const screenY = ((-projected.y + 1) * viewportHeight) / 2;
      const screenMidpoint = new THREE.Vector2(screenX, screenY);

      // Calculate distance from cursor to line midpoint
      const distance = mouse.distanceTo(screenMidpoint);

      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestResult = {
          line,
          snapPoint: midpoint,
          distancePixels: distance,
        };
      }
    }

    return nearestResult;
  }

  /**
   * Snap a prompt to a detected line's position and rotation.
   * Updates the prompt's worldPoint, direction, and lengths to match the line.
   */
  snapPromptToLine(prompt: LinePrompt, line: DetectedLine, floorHeight: number | null): void {
    // Calculate midpoint of the line, pin to the active base plane.
    const midpoint = line.start.clone().add(line.end).multiplyScalar(0.5);
    this.snapToBasePlane(midpoint, floorHeight);

    // Update prompt position to line midpoint
    prompt.worldPoint.copy(midpoint);

    // Update prompt direction to match the line direction
    prompt.direction.copy(line.direction.clone().normalize());

    // Calculate lengths from midpoint to line endpoints
    const halfLength = line.start.distanceTo(line.end) / 2;
    prompt.positiveLength = halfLength;
    prompt.negativeLength = halfLength;

    // Update screen position
    prompt.screenPosition = this.coordinateTransformer.worldToScreen(midpoint);
  }
}
